import { Injectable } from '@angular/core'
import { PresentedError } from 'src/utils/presented-error'

/**
 * A surface that can render an error in place. Registered while the surface is on screen and
 * unregistered when it closes, so the error system can find it without the call site knowing
 * anything about presentation.
 */
export interface ErrorAnchorHost {
  /** Stable identifier, e.g. `new-role-modal` or `launch-recovery`. Used for diagnostics. */
  id: string
  /**
   * Takes the error if this surface owns it, and returns true once rendered. Returning false
   * passes it to the next host out, and finally to the shell toast — the policy stays with
   * the host, which is the only thing that knows which controls it has.
   */
  accept(presented: PresentedError): boolean
}

/**
 * The "anchor before you announce" mechanism. `UiService` asks this registry before falling
 * back to a toast, so a validation failure lands on the input that caused it and a dialog
 * submit failure lands in that dialog.
 *
 * Hosts are consulted innermost-first: a field host inside a modal gets the chance to claim a
 * field-tier error before the modal itself sees it.
 *
 * Field-tier errors name the control they belong to, so any field host can be offered one and
 * will decline unless it owns that control. Everything else carries no such marker, so it is
 * only ever offered to the host the call site named — otherwise a background poll failure
 * would land in whatever dialog happened to be open.
 */
@Injectable({
  providedIn: 'root',
})
export class ErrorAnchorService {
  private hosts: ErrorAnchorHost[] = []

  /** Returns the deregistration callback; call it on destroy or the host outlives its surface. */
  register(host: ErrorAnchorHost): () => void {
    this.hosts = [...this.hosts, host]
    return () => {
      this.hosts = this.hosts.filter((candidate) => candidate !== host)
    }
  }

  /**
   * Offers the error to each eligible host, innermost first. Returns true when one rendered it,
   * meaning no toast should fire. `ownerIds` are the surfaces the call site named; without any,
   * only self-scoping field-tier errors are offered anywhere.
   *
   * Naming several is how one submit reaches the right one: a dialog and the panel behind it
   * hold different tiers, and innermost-first already asks the dialog first, since it
   * registered after the panel it opened over.
   */
  route(presented: PresentedError, ...ownerIds: (string | undefined)[]): boolean {
    const owners = ownerIds.filter((id): id is string => id !== undefined)
    if (presented.tier !== 'field' && owners.length === 0) return false

    const eligible = presented.tier === 'field' ? this.hosts : this.hosts.filter((host) => owners.includes(host.id))

    // Reverse rather than mutate: registration order is the nesting order.
    for (let index = eligible.length - 1; index >= 0; index--) {
      if (eligible[index].accept(presented)) return true
    }
    return false
  }

  /**
   * Whether the named surface is currently mounted. Sets `ErrorContext.anchored`, which is what
   * lets an action failure be tiered to a dialog rather than the corner of the screen.
   */
  ownsSurface(ownerId: string | undefined): boolean {
    return ownerId !== undefined && this.hosts.some((host) => host.id === ownerId)
  }

  /** Host ids, innermost last. Diagnostics only. */
  get registeredIds(): string[] {
    return this.hosts.map((host) => host.id)
  }
}
